import { useEffect, useState } from 'react';
import type { RuleDetails, RuleSummary } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../context/ArbiterClientContext';

interface UseRuleOptions {
  /** Numeric version or '@CURRENT'; omitted means the current version. */
  version?: string;
  includeDependencies?: boolean;
}

interface UseRuleResult {
  rule: RuleDetails | null;
  parents: RuleSummary[];
  children: RuleSummary[];
  loading: boolean;
  error: Error | null;
}

type RuleData = Pick<UseRuleResult, 'rule' | 'parents' | 'children'>;
const EMPTY: RuleData = { rule: null, parents: [], children: [] };

/**
 * DescribeRule for one rule; refetches when the name or requested version changes. The previous result
 * stays available across a version switch of the same rule, but never across a change of rule name.
 */
export function useRule(ruleName: string | undefined, { version, includeDependencies }: UseRuleOptions = {}): UseRuleResult {
  const client = useArbiterClient();
  const [data, setData] = useState<RuleData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ruleName) {
      setData(EMPTY);
      setError(null);
      setLoading(false);
      return;
    }
    // A later request (version switch) or an unmount makes this response stale; ignore it.
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData((prev) => (prev.rule?.ruleName === ruleName ? prev : EMPTY));
    client.describeRule({ ruleName, version, includeDependencies })
      .then((res) => {
        if (!cancelled) setData({ rule: res.rule ?? null, parents: res.parents ?? [], children: res.children ?? [] });
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ruleName, version, includeDependencies, client]);

  return { ...data, loading, error };
}
